<template>
    <v-container fluid class="leaderboard-container">
        <!-- Header -->
        <v-row align="center" class="py-2 px-2">
            <v-btn icon @click="$router.push({ name: 'search' })">
                <v-icon>mdi-arrow-left</v-icon>
            </v-btn>
            <v-spacer></v-spacer>
            <span class="leaderboard-title">{{ $t('pages.leaderboards.title') }}</span>
            <v-spacer></v-spacer>
            <hall-fames @click="$router.push({ name: 'hallfames' })" />
        </v-row>

        <!-- Tabs -->
        <v-row class="mt-2">
            <v-slide-group v-model="tab" class="leaderboard-tabs" >
                <v-slide-item v-for="t in leaderboardslide" :key="t.value" :value="t.value" v-slot="{ active, toggle }" class="mx-1" >
                <v-btn rounded class="leaderboard-category" :class="{ active }" @click="toggle" >
                    <div class="btn-stack">
                        <popular-icon v-if="t.value == 'popular_creators'" :color="active ? '#FF7805' : 'white'" :opacity="!active ? '0.6' : ''" /> 
                        <rising-star v-if="t.value == 'rising_stars'" :color="active ? '#FF7805' : 'white'" :opacity="!active ? '0.6' : ''"/>
                        <streamer-icon v-if="t.value == 'streamers'" :color="active ? '#FF7805' : 'white'" :opacity="!active ? '0.6' : ''"/>
                        <tipper-icon v-if="t.value == 'top_tippers'" :color="active ? '#FF7805' : 'white'" :opacity="!active ? '0.6' : ''"/>
                        <moderator-icon v-if="t.value == 'moderators'" :color="active ? '#FF7805' : 'white'" :opacity="!active ? '0.6' : ''"/>
                        {{ t.label }}
                    </div>
                </v-btn>
                </v-slide-item>
            </v-slide-group>
        </v-row>

        <!-- Podium -->
        <template>
            <v-row
                class="podium-row mt-6"
                justify="center"
                v-if="data.length > 0"
            >
                <div
                    v-for="podium in podiumItems"
                    :key="podium.key"
                    :class="['col-4', 'podium', `podium-${podium.rank}`, 'px-0']"
                    >
                        <crown-leaderboard v-if="podium.rank === 1" class="crown" />
                        <div v-if="podium.item">
                            <profile-image
                                v-if="podium.item.profileImage"
                                :img="podium.item.profileImage"
                                :id="currentUser.id"
                                :username="podium.item.user.name || ''"
                                :is-exist-story="podium.item.creatorHasStories"
                                :has-not-watched-stories="podium.item.hasNotWatchedStories"
                                :size="40"
                                :fontSize="18"
                                :border="!podium.item.creatorHasStories ? 1 : 2"
                                class="profile-image"
                                :view-profile="true"
                                :view-profile-from="'leaderboard'"
                                :view-profile-slug="podium.item.user.slug"
                            />
                            <span class="username">
                                {{ podium.item.user.name || 'Calculating…' }}
                                <verified-icon v-if="podium.item.user.verify_at" :height="15" :width="15" class="ml-1" />
                                <holiday-mood-icon v-if="podium.item.user.creator.on_holiday_mood"/>
                            </span>
                        </div>
                        <div v-else>Calculating…</div>
                        <div :class="['podium-block', podium.class]">
                            <span v-if="podium.rank === 1"></span>{{ podium.rank }}
                        </div>
                    </div>
            </v-row>
            
            <div v-else class="d-flex align-center justify-center h-100 mt-4">
                No Data Found
            </div>
        </template>

        <!-- Rest of ranks -->
        <div class="rank-cards">
            <div
                v-for="(item, index) in filteredRanks"
                :key="item.id"
                class="rank-card"
                :class="{'active': item.user && item.user.id === currentUser.id}"
            >
                    <profile-image 
                        v-if="item.profileImage"
                        :img="item.profileImage"
                        :id="currentUser.id"
                        :username="item.user.name"
                        :is-exist-story="item.creatorHasStories"
                        :has-not-watched-stories="item.hasNotWatchedStories"
                        class="profile-image"
                        :size="40"
                        :fontSize="18"
                        :border="!item.creatorHasStories ? 1 : 2"
                        :class="{'user-rank': item.user && item.user.id === currentUser.id}"
                        :view-profile="true"
                        :view-profile-from="'leaderboard'"
                        :view-profile-slug="item.user.slug"
                         />
                <div class="rank-card-content">
                <span class="name">{{ item.user && item.user.name ? item.user.name : 'Unknown User' }}</span>
                <span class="score">
                    {{ data.length >= 3 ? index + 4 : index + 1 }}
                </span>
                </div>
            </div>
        </div>

        <!-- Footer -->
        <v-row justify="center" class="mt-4">
            <small class="reset-text">Resets in {{ countdown }}</small>
        </v-row>
    </v-container>
</template>

<script>
import { mapActions, mapMutations, mapState } from "vuex";
import CrownLeaderboard from "@/assets/images/icons/crown-leaderboard.svg?inline";
import hallFames from "@/assets/images/icons/hall-fames.svg?inline";
import PopularIcon from "@/components/app/icons/PopularIcon";
import RisingStar from "@/components/app/icons/RisingStarIcon";
import StreamerIcon from "@/components/app/icons/StreamerIcon";
import TipperIcon from "@/components/app/icons/TipperIcon";
import ModeratorIcon from "@/components/app/icons/ModeratorIcon";
import ProfileImage from "@/components/app/profile/ProfileImage";
import VerifiedIcon from "@/components/app/common/VerifiedIcon";
import HolidayMoodIcon from "@/components/app/icons/HolidayMoodIcon";

export default {
    name: "LeaderBoard",
    components: {PopularIcon, CrownLeaderboard, hallFames, RisingStar, StreamerIcon, TipperIcon, ModeratorIcon, ProfileImage, VerifiedIcon, HolidayMoodIcon },
    data: () => ({
        tab: "popular_creators",
        targetDate: null,
        countdown: '',
        intervalId: null,
        leaderboardData: {},
        leaderboardslide: [
            { value: 'popular_creators', label: 'Popular' },
            { value: 'rising_stars', label: 'Rising Stars' },
            { value: 'streamers', label: 'Streamers' },
            { value: 'top_tippers', label: 'Top Tippers' },
            { value: 'moderators', label: 'Moderators' }
        ],
    }),
    computed: {
        ...mapState({
            leaderboard: (state) => state.user?.user?.leaderboard || {},
            currentUser: (state) => state.user.user.data,
        }),
        data() {
            return this.leaderboard?.[this.tab] ||  this.leaderboardData?.[this.tab] || [];
        },
        filteredRanks() {
            return this.data.length >= 3 ? this.data.slice(3) : this.data.slice(3);
        },
        podiumItems() {
            if (!this.data.length) return [];

            return [
                { rank: 2, item: this.data[1] || null, class: 'silver', key: 'rank-2' }, 
                { rank: 1, item: this.data[0] || null, class: 'gold', key: 'rank-1' },   
                { rank: 3, item: this.data[2] || null, class: 'bronze', key: 'rank-3' }  
            ];
        },
    },
    mounted() {
        if (!this.leaderboard || Object.keys(this.leaderboard).length === 0) {
            this.fetchLeaderBoard();
        }
        this.targetDate = new Date();
        this.targetDate.setDate(this.targetDate.getDate() + 30);

        this.updateCountdown();
        this.intervalId = setInterval(this.updateCountdown, 1000);
    },
    beforeUnmount() {
        clearInterval(this.intervalId)
    },
    methods: {
        ...mapActions(["getLeaderBoard"]),
        ...mapMutations(["setLeaderBoard","showSnackBar"]),
        async fetchLeaderBoard () {
            try {
                if (Object.keys(this.leaderboard).length > 0) {
                    return; 
                }

                const res = await this.getLeaderBoard();
                this.leaderboardData = res;
            } catch (e) {
                this.showSnackBar({ message: "Failed to load leaderboard", color: "error" });
            }
        },
        updateCountdown() {
            const now = new Date();
            const endOfMonth = new Date( now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59 );

            const diff = endOfMonth.getTime() - now.getTime();

            if (diff <= 0) {
                this.countdown = "Expired";
                clearInterval(this.timer);
                return;
            }

            const days = Math.floor(diff / (1000 * 60 * 60 * 24));
            const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
            const minutes = Math.floor((diff / (1000 * 60)) % 60);

            this.countdown = `${days}d ${hours}h ${minutes}m`;
        },
    }
};
</script>

<style scoped>


.podium .avatar { width: 50px; height: 50px; border-radius: 50%; margin-bottom: 4px; border: 2px solid #fff; }
.rank-card .avatar { width: 50px; height: 50px; border-radius: 50%; margin-bottom: 4px; border: 2px solid #fff; }
.profile-image { margin: 0 auto;  position: relative; }
/* z-index: 999; */
.crown { margin-bottom: -15px; }

.leaderboard-title { font-size: 24px; line-height: 32px; font-weight: 700; color: #FFFFFF; }
.leaderboard-tabs { display: flex; gap: 10px; border-radius: 0; }
.leaderboard-tabs button { height: 100% !important; min-width: 110px !important; display: block; border-radius: 10px !important; border: 1px solid rgba(255, 255, 255, 0.2) !important; padding: 24px 16px !important; }
.leaderboard-category .btn-stack { display: flex; flex-direction: column; align-items: center; justify-content: center; font-size: 16px; line-height: 24px; font-weight: 600; color: rgba(255, 255, 255, 0.6); letter-spacing: 0.2px; text-transform: capitalize; }
.leaderboard-category .btn-stack svg { margin-bottom: 6px; }
.leaderboard-category button.active .btn-stack { color: #FFFFFF; }

.leaderboard-tabs button.active { background: rgba(255, 120, 5, 0.16) !important; color: #FFFFFF !important; border: 1.5px solid rgba(255, 120, 5, 1) !important; }

.podium-row { align-items: end; padding: 0 12px; }
.podium-row .podium { text-align: center; }
.podium-row .podium img { height: 50px; width: 50px; }
.podium-row .podium.podium-1 img { height: 72px; width: 72px; margin-top: -13px; } 
.podium-row .podium .username { display: block; font-size: 13px; font-weight: 500; line-height: 28px; margin-bottom: 11px; }
.podium-row .podium .podium-block { height: 135px; position: relative; display: flex; align-items: center; justify-content: center; background-color: rgba(255, 255, 255, 0.1); font-size: 72px; font-weight: 800; line-height: 1; color: rgba(255, 120, 5, 1); }
.podium-row .podium .podium-block.gold { height: 200px; background-color: rgba(255, 120, 5, 1); color: #000000; font-size: 80px; font-weight: 800; line-height: 1; }

.podium-row .podium .podium-block.gold span { width: 100%; height: 15px; position: absolute; top: 0px; left: 0px; background: rgba(255, 176, 110, 1); display: block; }
.podium-row .podium .podium-block.silver { height: 165px; font-size: 80px; font-weight: 800; line-height: 1; }

.podium-row .podium .podium-block.gold:before { width: 15px; height: 22px; content: ""; position: absolute; top: -8px; left: -7px; background: rgba(0, 0, 0, 1); z-index: 1; transform: rotate(45deg); }
.podium-row .podium .podium-block.gold:after { width: 15px; height: 22px; content:""; position: absolute; top: -8px; right: -7px; background: rgba(0, 0, 0, 1); transform: rotate(-45deg); }

.podium-row .podium .podium-block.silver:before { width: 15px; height: 22px; content: ""; position: absolute; top: -8px; left: -7px; background: rgba(0, 0, 0, 1); z-index: 1; transform: rotate(45deg); }
.podium-row .podium .podium-block.silver:after { width: 100%; height: 15px; content:""; position: absolute; top: 0px; left: 0px; background: rgba(255, 255, 255, 0.15); }

.podium-row .podium .podium-block.bronze:before { width: 15px; height: 22px; content: ""; position: absolute; top: -8px; right: -7px; background: rgba(0, 0, 0, 1); z-index: 1; transform: rotate(-45deg); }
.podium-row .podium .podium-block.bronze:after { width: 100%; height: 15px; content:""; position: absolute; top: 0px; left: 0px; background: rgba(255, 255, 255, 0.15); }


.rank-cards { margin-top: 40px; display: flex; flex-direction: column; gap: 16px; }
.rank-cards .rank-card { display: flex; align-items: center; gap: 10px; background: rgba(23, 23, 23, 1); border: 1px solid rgba(255, 255, 255, 0.05); border-radius: 8px; padding: 8px 16px; }
.rank-cards .rank-card.active { background: linear-gradient(354.8deg, #FF7805 4.66%, #FFA702 83.25%); border: 1px solid rgba(255, 120, 5, 1); }

.rank-cards .rank-card img { height: 40px; width: 40px; }
/* .rank-cards .rank-card .user-rank { outline: dashed 1.5px #FFFFFF; outline-offset: 2px; } */
.rank-cards .rank-card .rank-card-content { width: calc(100% - 40px); display: flex; align-items: center; justify-content: space-between; }
.rank-cards .rank-card .rank-card-content .name { font-weight: 500; font-size: 18px; line-height: 30px; }
.rank-cards .rank-card .rank-card-content .score { font-weight: 700; font-size: 24px; line-height: 1; }

.reset-text { position: fixed; bottom: 20px; left: 50%; transform: translateX(-50%); font-weight: 700; font-size: 14px; line-height: 20px; letter-spacing: 0.2px; color: rgba(255, 255, 255, 0.6); }

</style>
